import { Button, VisuallyHidden, type ButtonProps } from '@artosera/ui';
import { formatDate, type Action, type Module } from '@artosera/shared';
import { Lock } from 'lucide-react';
import {
  Children,
  createContext,
  isValidElement,
  useContext,
  useState,
  type FormEvent,
  type JSX,
  type ReactElement,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router';
import type { GalerieSession } from '../../api/types.js';
import { actionVerrouillee } from '../../auth/lectureSeule.js';
import { moduleEnLectureSeule } from '../../auth/sujet.js';
import styles from './LectureSeule.module.css';

/**
 * Lecture seule d'un module (« tout voir, rien créer »), côté écran.
 *
 * `RegieShell` pose le contexte autour de la page : le module de la route, la
 * galerie de la session et la galerie de la page (celle sans écriture, voir
 * `auth/lectureSeule.ts`). Les pages n'ont rien à lire pour être en lecture
 * seule — leurs gardes `galerieALaction` suffisent. Ce fichier ne sert qu'à
 * MONTRER le verrou là où l'écran garde la place d'un bouton :
 *
 *  - `useLectureSeuleModule()` : la page est-elle en lecture seule, et cette
 *    action en a-t-elle été retirée ?
 *  - `BoutonVerrouille` : la place du bouton, désactivée, avec son cadenas.
 *  - `BandeauLectureSeule` : le bandeau de tête de page.
 *  - `EtiquetteLectureSeule` : la pastille d'une section en lecture seule dans
 *    une page du cœur.
 */

export interface ContexteLectureSeule {
  /** Le module de la page affichée, s'il est en lecture seule ; sinon `null`. */
  readonly module: Module | null;
  readonly galerieReelle: GalerieSession;
  readonly galeriePage: GalerieSession;
}

const Contexte = createContext<ContexteLectureSeule | null>(null);

export function LectureSeuleProvider({
  valeur,
  children,
}: {
  valeur: ContexteLectureSeule;
  children: ReactNode;
}): JSX.Element {
  return <Contexte.Provider value={valeur}>{children}</Contexte.Provider>;
}

export interface EtatLectureSeule {
  /** La page est celle d'un module en lecture seule. */
  readonly actif: boolean;
  readonly module: Module | null;
  /** Cette action d'écriture a-t-elle été retirée par la lecture seule ? */
  readonly verrouillee: (action: Action) => boolean;
  /** Ce module est-il en lecture seule pour la galerie (où que la page se trouve) ? */
  readonly moduleEnLecture: (module: Module) => boolean;
}

const HORS_CONTEXTE: EtatLectureSeule = {
  actif: false,
  module: null,
  verrouillee: () => false,
  moduleEnLecture: () => false,
};

/** Hors coquille (tests d'un écran isolé), rien n'est verrouillé. */
export function useLectureSeuleModule(): EtatLectureSeule {
  const contexte = useContext(Contexte);
  if (!contexte) return HORS_CONTEXTE;
  return {
    actif: contexte.module !== null,
    module: contexte.module,
    verrouillee: (action) =>
      actionVerrouillee(contexte.galerieReelle, contexte.galeriePage, action),
    moduleEnLecture: (module) => moduleEnLectureSeule(contexte.galerieReelle, module),
  };
}

/** Le cadenas des icônes de la régie : trait fin, jamais seul (texte ou libellé accessible à côté). */
export function Cadenas({ size = 14 }: { size?: number }): JSX.Element {
  return <Lock className={styles.cadenas} size={size} strokeWidth={1.75} aria-hidden />;
}

export interface BoutonVerrouilleProps extends Omit<
  ButtonProps,
  'variant' | 'disabled' | 'loading' | 'onClick' | 'type'
> {
  /**
   * L'écriture que ce bouton remplace, quand la lecture seule la retire de la
   * page : le bouton n'existe que si c'est le cas. Un rôle sans droit
   * d'écriture n'a pas de cadenas, il n'a pas le bouton.
   */
  action?: Action;
  /**
   * Le module en lecture seule, pour une écriture PARTAGÉE avec le cœur
   * (`documents:write`…) que la lecture seule ne retire pas de la page : le
   * bouton n'existe que si ce module est en lecture seule.
   */
  module?: Module;
}

/**
 * La place d'un bouton d'écriture dans un module en lecture seule : le même
 * libellé, un cadenas, un contour en tirets, `aria-disabled` — jamais
 * exécutable, mais focalisable pour que le lecteur d'écran en lise la raison.
 */
export function BoutonVerrouille({
  action,
  module,
  children,
  className,
  title,
  ...reste
}: BoutonVerrouilleProps): JSX.Element | null {
  const { t } = useTranslation('regie');
  const { verrouillee, moduleEnLecture } = useLectureSeuleModule();
  const retire =
    (action !== undefined && verrouillee(action)) ||
    (module !== undefined && moduleEnLecture(module));
  if ((action !== undefined || module !== undefined) && !retire) return null;
  return (
    <Button
      {...reste}
      variant="secondary"
      className={[styles.verrouille, className].filter(Boolean).join(' ')}
      aria-disabled="true"
      data-verrouille="lecture-seule"
      title={title ?? t('module.lectureSeule.action')}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
    >
      <Cadenas />
      {children}
      <VisuallyHidden>{`. ${t('module.lectureSeule.action')}`}</VisuallyHidden>
    </Button>
  );
}

/**
 * La place d'un bouton d'écriture, à poser AUTOUR du bouton : ouvert, il reste
 * tel quel ; fermé par la lecture seule, il devient `BoutonVerrouille` (même
 * libellé, même taille, cadenas à la place de l'icône) ; fermé pour une autre
 * raison (rôle sans droit), il disparaît comme avant.
 *
 *   <Verrouillable action="invoices:write" ouvert={peutFacturer}>
 *     <Button size="sm" onClick={…}>Nouvelle facture</Button>
 *   </Verrouillable>
 *
 * `module` remplace `action` pour une écriture partagée avec le cœur.
 */
export function Verrouillable({
  action,
  module,
  ouvert,
  applicable = true,
  children,
}: {
  action?: Action;
  module?: Module;
  /** Le geste est ouvert à cette galerie : le bouton est rendu tel quel. */
  ouvert: boolean;
  /**
   * Le geste a un sens dans l'état de la page (« Créer un avoir » sur une
   * facture émise, pas sur un brouillon). Faux : ni bouton, ni cadenas.
   */
  applicable?: boolean;
  children: ReactElement<ButtonProps>;
}): JSX.Element | null {
  if (!applicable) return null;
  if (ouvert) return children;
  const { size } = children.props;
  // Le cadenas remplace l'icône du bouton ouvert : les icônes (`aria-hidden`) ne sont pas reprises.
  const libelle = Children.toArray(children.props.children).filter(
    (enfant) =>
      !(
        isValidElement<{ 'aria-hidden'?: boolean | string }>(enfant) && enfant.props['aria-hidden']
      ),
  );
  return (
    <BoutonVerrouille
      {...(action ? { action } : {})}
      {...(module ? { module } : {})}
      {...(size ? { size } : {})}
    >
      {libelle}
    </BoutonVerrouille>
  );
}

/**
 * Libellé d'un onglet d'une page du cœur qui montre un module : le cadenas, et
 * « lecture seule » pour les lecteurs d'écran, quand ce module n'est que
 * consultable. Sinon, le libellé tel quel.
 */
export function LibelleOnglet({
  module,
  children,
}: {
  module: Module;
  children: string;
}): JSX.Element {
  const { t } = useTranslation('regie');
  const { moduleEnLecture } = useLectureSeuleModule();
  if (!moduleEnLecture(module)) return <>{children}</>;
  return (
    <span className={styles.libelleOnglet}>
      {children}
      <Cadenas size={12} />
      <VisuallyHidden>{`, ${t('module.lectureSeule.etiquette').toLowerCase()}`}</VisuallyHidden>
    </span>
  );
}

/**
 * Bandeau sobre en tête d'une page de module en lecture seule. `refus` ajoute
 * la raison d'un geste qui vient d'être arrêté.
 */
export function BandeauLectureSeule({ refus = false }: { refus?: boolean }): JSX.Element {
  const { t, i18n } = useTranslation('regie');
  const contexte = useContext(Contexte);
  const addon = contexte?.galerieReelle.addonsLectureSeule?.find(
    (ligne) => ligne.addon === contexte.module,
  );
  return (
    <div className={styles.bandeau} role="status" data-testid="bandeau-lecture-seule">
      <Cadenas size={16} />
      <p>
        {t('module.lectureSeule.bandeau')}
        {addon ? (
          <>
            {' '}
            {t('reglages.marketplace.masqueLe', {
              date: formatDate(
                new Date(addon.masqueLe),
                i18n.language === 'en' ? 'en' : 'fr',
                'court',
              ),
            })}{' '}
            <Link to={`/${contexte!.galerieReelle.slug}/reglages/addons?onglet=catalogue`}>
              {t('reglages.marketplace.voirCatalogue')}
            </Link>
          </>
        ) : null}
        {refus ? <strong> {t('module.lectureSeule.refus')}</strong> : null}
      </p>
    </div>
  );
}

/**
 * Formulaires qui ne modifient rien (recherche, saisie d'un code à consulter) :
 * ils se soumettent aussi en lecture seule.
 */
const FORMULAIRE_DE_CONSULTATION = '[role="search"], [data-lecture-seule="ok"]';

/**
 * Le contenu d'une page de module en lecture seule : le bandeau, puis la page.
 *
 * Filet de sécurité : un formulaire que la page aurait laissé ouvert (réglage
 * personnel, case à cocher sans garde `galerieALaction`) ne part pas. Sa
 * soumission est arrêtée avant d'atteindre l'écran, et le bandeau dit
 * pourquoi — l'API, de toute façon, la refuserait par 403 `module_lecture_seule`.
 */
export function PageEnLectureSeule({ children }: { children: ReactNode }): JSX.Element {
  // La page où un geste a été arrêté : en changer efface le message.
  const [refusSur, setRefusSur] = useState<string | null>(null);
  const { pathname } = useLocation();
  function arreter(evenement: FormEvent<HTMLElement>): void {
    const cible = evenement.target;
    if (cible instanceof HTMLFormElement && cible.matches(FORMULAIRE_DE_CONSULTATION)) return;
    evenement.preventDefault();
    evenement.stopPropagation();
    setRefusSur(pathname);
  }
  return (
    <div className={styles.zone} onSubmitCapture={arreter}>
      <BandeauLectureSeule refus={refusSur === pathname} />
      {children}
    </div>
  );
}

/** Pastille « Lecture seule » d'une section de module dans une page du cœur. */
export function EtiquetteLectureSeule(): JSX.Element {
  const { t } = useTranslation('regie');
  return (
    <span className={styles.etiquette} data-testid="etiquette-lecture-seule">
      <Cadenas size={12} />
      {t('module.lectureSeule.etiquette')}
    </span>
  );
}
